import React, { useState, useEffect, useRef } from "react";
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";
import { PencilIcon, TrashIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ConfirmModal } from "components/shared/ConfirmModal";

// Custom Option rendering to display Edit and Delete icons, or inline input if editing
const CustomOption = (props) => {
  const { data, isFocused, selectProps } = props;
  const isNew = data.__isNew__;

  const isEditing = selectProps.editingOptionId === data.value;
  const editingText = selectProps.editingText;
  const setEditingText = selectProps.setEditingText;

  const handleSaveEdit = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (editingText.trim()) {
      selectProps.onEditOption?.(data.value, data.label, editingText.trim());
    }
    selectProps.stopEditingOption();
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    e.preventDefault();
    selectProps.stopEditingOption();
  };

  if (isEditing) {
    return (
      <components.Option {...props}>
        <div
          className="flex items-center gap-2 w-full"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
        >
          <input
            type="text"
            className="flex-1 min-w-0 text-sm border border-gray-350 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-800 dark:text-slate-100"
            value={editingText}
            onChange={(e) => setEditingText(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") {
                e.preventDefault();
                handleSaveEdit(e);
              } else if (e.key === "Escape") {
                e.preventDefault();
                handleCancelEdit(e);
              }
            }}
            autoFocus
          />
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={handleSaveEdit}
            className="p-1 rounded text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-950/30 transition-colors cursor-pointer shrink-0"
          >
            <CheckIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={handleCancelEdit}
            className="p-1 rounded text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30 transition-colors cursor-pointer shrink-0"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </components.Option>
    );
  }

  return (
    <components.Option {...props}>
      <div className="flex items-center justify-between w-full group">
        <div className="break-words whitespace-normal flex-1 flex items-center gap-2.5" title={data.label}>
          {/* Checkbox for multi-select mode */}
          {selectProps.isMulti && !isNew && (
            <input
              type="checkbox"
              readOnly
              checked={props.isSelected}
              onMouseDown={(e) => e.stopPropagation()}
              className="w-4 h-4 shrink-0 rounded border-gray-300 dark:border-slate-600 accent-blue-600 cursor-pointer pointer-events-none"
            />
          )}
          {props.children}
        </div>
        {!isNew &&
          ((selectProps.onEditOption && (!selectProps.isOptionEditable || selectProps.isOptionEditable(data))) ||
            (selectProps.onDeleteOption && (!selectProps.isOptionDeletable || selectProps.isOptionDeletable(data)))) && (
          <div
            className={`flex items-center gap-1 shrink-0 transition-opacity duration-150 opacity-0 group-hover:opacity-100`}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            {selectProps.onEditOption && (!selectProps.isOptionEditable || selectProps.isOptionEditable(data)) && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  selectProps.blockMenuCloseRef.current = true;
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (selectProps.useModal) {
                    selectProps.onEditOptionClick?.(data.value, data.label);
                    setTimeout(() => {
                      selectProps.setMenuIsOpen(false);
                    }, 50);
                  } else {
                    selectProps.startEditingOption(data.value, data.label);
                  }
                }}
                className={`p-1 rounded transition-colors cursor-pointer ${
                  props.isSelected
                    ? "text-white/70 hover:text-white hover:bg-white/20"
                    : isFocused
                    ? "text-blue-500 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/30"
                    : "text-gray-400 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-black/5 dark:hover:bg-white/10"
                }`}
                title="Edit Option"
              >
                <PencilIcon className="w-3.5 h-3.5" />
              </button>
            )}
            {selectProps.onDeleteOption && (!selectProps.isOptionDeletable || selectProps.isOptionDeletable(data)) && (
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (selectProps.onDeleteOption) {
                    if (selectProps.bypassInternalConfirm) {
                      selectProps.onDeleteOption(data.value, data.label);
                      setTimeout(() => {
                        selectProps.setMenuIsOpen(false);
                      }, 50);
                    } else {
                      selectProps.triggerDeleteConfirmation(data.value, data.label);
                    }
                  }
                }}
                className={`p-1 rounded transition-colors cursor-pointer ${
                  props.isSelected
                    ? "text-white/70 hover:text-rose-200 hover:bg-white/20"
                    : isFocused
                    ? "text-red-500 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
                    : "text-gray-400 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 hover:bg-black/5 dark:hover:bg-white/10"
                }`}
                title="Delete Option"
              >
                <TrashIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
    </components.Option>
  );
};

// Custom MenuList rendering to include "+ Add New Option" as an inline input when clicked
const CustomMenuList = (props) => {
  const { selectProps } = props;
  const hasOnCreate = selectProps.onCreateOptionClick || selectProps.onCreateOption;
  const addNewText = hasOnCreate ? (selectProps.addNewText || "+ Add New Option") : null;
  const menuHeader = selectProps.menuHeader ? selectProps.menuHeader : selectProps.placeholder;

  const isAdding = selectProps.isAdding;
  const setIsAdding = selectProps.setIsAdding;
  const newVal = selectProps.newVal;
  const setNewVal = selectProps.setNewVal;
  const handleSave = selectProps.handleSave;
  const handleCancel = selectProps.handleCancel;

  return (
    <components.MenuList {...props}>
      <div className="flex flex-col bg-white dark:bg-slate-900 border-b border-gray-150 dark:border-slate-800">
        {menuHeader && (
          <div className="px-3.5 py-2 text-slate-500 dark:text-slate-400 font-medium text-xs bg-slate-50 dark:bg-slate-800/50 border-b border-gray-100 dark:border-slate-800">
            {menuHeader}
          </div>
        )}
        {isAdding ? (
          <div
            className="px-3.5 py-2 flex items-center gap-2 border-b border-gray-100 dark:border-slate-800"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            <input
              type="text"
              className="flex-1 min-w-0 text-sm border border-gray-350 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-800 dark:text-slate-100"
              placeholder={`Type new...`}
              value={newVal}
              onChange={(e) => setNewVal(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSave();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  handleCancel();
                }
              }}
              autoFocus
            />
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={handleSave}
              className="p-1 rounded text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-950/30 transition-colors cursor-pointer"
            >
              <CheckIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={handleCancel}
              className="p-1 rounded text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30 transition-colors cursor-pointer"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        ) : (
          addNewText && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (selectProps.useModal) {
                  selectProps.onCreateOptionClick?.();
                  setTimeout(() => {
                    selectProps.setMenuIsOpen(false);
                  }, 50);
                } else {
                  setIsAdding(true);
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault(); // prevent react-select from detecting blur → onMenuClose
                selectProps.blockMenuCloseRef.current = true;
              }}
              className="px-3.5 py-2.5 text-blue-600 dark:text-blue-400 font-bold text-sm hover:bg-blue-50 dark:hover:bg-slate-800 cursor-pointer flex items-center gap-1.5 transition-colors border-b border-gray-100 dark:border-slate-800"
            >
              {addNewText}
            </div>
          )
        )}
      </div>
      {props.children}
    </components.MenuList>
  );
};

// Custom SingleValue rendering
const CustomSingleValue = (props) => {
  return (
    <components.SingleValue {...props}>
      {props.children}
    </components.SingleValue>
  );
};

export default function CustomCreatableDropdown({
  options = [],
  value,
  onChange,
  onCreateOption,
  onEditOption,
  onDeleteOption,
  onDisabledClick,
  placeholder,
  isLoading = false,
  error = false,
  menuHeader,
  addNewText,
  isClearable = true,
  isSearchable = true,
  isMulti = false,
  deleteConfirmationMessage,
  useModal = false,
  height = "42px",
  minHeight = "42px",
  ...props
}) {
  const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newVal, setNewVal] = useState("");

  const [editingOptionId, setEditingOptionId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [inputValue, setInputValue] = useState("");

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const containerRef = useRef(null);
  // Tracks whether an interactive element (Add / Edit btn) was just clicked
  // so that the react-select onMenuClose guard can block a premature close.
  const blockMenuCloseRef = useRef(false);

  const triggerDeleteConfirmation = (value, label) => {
    setPendingDelete({ value, label });
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (pendingDelete) {
      onDeleteOption?.(pendingDelete.value, pendingDelete.label, true);
    }
    setShowConfirmModal(false);
    setPendingDelete(null);
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setPendingDelete(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const isInsideContainer = containerRef.current && containerRef.current.contains(target);
      const isInsideMenu = target.closest && (target.closest(".react-select__menu") || target.closest(".react-select__menu-portal"));

      if (!isInsideContainer && !isInsideMenu) {
        setMenuIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    document.addEventListener("pointerdown", handleClickOutside, true);
    document.addEventListener("touchstart", handleClickOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("pointerdown", handleClickOutside, true);
      document.removeEventListener("touchstart", handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    if (!menuIsOpen) {
      setIsAdding(false);
      setNewVal("");
      setEditingOptionId(null);
      setEditingText("");
      setInputValue("");
    }
  }, [menuIsOpen]);

  const handleSave = async () => {
    const text = newVal.trim();
    if (text) {
      const res = await onCreateOption?.(text);
      if (res && res.success && res.optionId) {
        const newOption = { value: res.optionId, label: text };
        if (isMulti) {
          const existing = Array.isArray(value) ? value : (value ? [value] : []);
          onChange?.([...existing, newOption]);
          setIsAdding(false);
          setNewVal("");
          setMenuIsOpen(true); // keep menu open in multi mode
          return;
        } else {
          onChange?.(newOption);
        }
      }
    }
    setIsAdding(false);
    setNewVal("");
    setMenuIsOpen(false);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setNewVal("");
    setMenuIsOpen(true);
  };

  const startEditingOption = (id, currentLabel) => {
    setEditingOptionId(id);
    setEditingText(currentLabel);
  };

  const stopEditingOption = () => {
    setEditingOptionId(null);
    setEditingText("");
  };

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: minHeight,
      height: isMulti ? "auto" : height,
      borderRadius: "0.5rem",
      borderColor: error
        ? (isDark ? "#f87171" : "#ef4444")
        : state.isDisabled
        ? (isDark ? "#334155" : "#cbd5e1")
        : (isDark ? "#334155" : "#d1d5db"),
      backgroundColor: state.isDisabled
        ? (isDark ? "#1e293b" : "#f1f5f9")
        : (isDark ? "#1e293b" : "#ffffff"),
      opacity: 1,
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      boxShadow: "none",
      color: isDark ? "#f1f5f9" : "#1f2937",
      "&:hover": {
        borderColor: error
          ? (isDark ? "#f87171" : "#ef4444")
          : state.isDisabled
          ? (isDark ? "#334155" : "#cbd5e1")
          : (isDark ? "#475569" : "#9ca3af"),
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: isMulti ? "4px 8px" : "0 12px",
      flexWrap: isMulti ? "wrap" : "nowrap",
      gap: "2px",
      overflow: "hidden",
    }),
    input: (provided) => ({
      ...provided,
      color: isDark ? "#f1f5f9" : "#1f2937",
      margin: "0",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: isDark ? "rgba(37,99,235,0.2)" : "#dbeafe",
      borderRadius: "9999px",
      padding: "0 2px",
      margin: "1px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: isDark ? "#93c5fd" : "#1d4ed8",
      fontSize: "12px",
      fontWeight: "500",
      paddingLeft: "8px",
      paddingRight: "2px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: isDark ? "#93c5fd" : "#1d4ed8",
      borderRadius: "9999px",
      cursor: "pointer",
      ":hover": {
        backgroundColor: isDark ? "rgba(37,99,235,0.4)" : "#bfdbfe",
        color: isDark ? "#fff" : "#1e40af",
      },
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      opacity: state.isDisabled ? 0.6 : 1,
      cursor: state.isDisabled ? "not-allowed" : "pointer",
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? "#ffffff" : state.isFocused ? (isDark ? "#ffffff" : "#1f2937") : (isDark ? "#cbd5e1" : "#1f2937"),
      backgroundColor: state.isSelected
        ? "#2563eb"
        : state.isFocused
        ? (isDark ? "#334155" : "#f1f5f9")
        : "transparent",
      cursor: "pointer",
      fontSize: "14px",
      "&:active": {
        backgroundColor: state.isSelected ? "#2563eb" : (isDark ? "#475569" : "#e2e8f0"),
      },
    }),
    singleValue: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? (isDark ? "#94a3b8" : "#64748b") : (isDark ? "#f1f5f9" : "#1f2937"),
      fontSize: "14px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      backgroundColor: isDark ? "#1e293b" : "#ffffff",
      border: isDark ? "1px solid #334155" : "1px solid #e5e7eb",
      borderRadius: "0.5rem",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: state.isDisabled
        ? (isDark ? "#94a3b8" : "#64748b")
        : (isDark ? "#64748b" : "#475569"),
      fontSize: "13px",
      fontWeight: state.isDisabled ? "400" : "300",
      fontStyle: state.isDisabled ? "italic" : "normal",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
  };

  return (
    <div
      ref={containerRef}
      className="w-full"
      onClickCapture={(e) => {
        if (props.isDisabled && onDisabledClick) {
          onDisabledClick(e);
        }
      }}
    >
      <CreatableSelect
        isClearable={isClearable}
        isSearchable={isSearchable}
        isMulti={isMulti}
        hideSelectedOptions={props.hideSelectedOptions !== undefined ? props.hideSelectedOptions : false}
        closeMenuOnSelect={!isMulti}
        closeMenuOnScroll={true}
        options={options}
        value={value}
        onChange={(newValue, actionMeta) => {
          onChange?.(newValue, actionMeta);
          if (!isMulti) {
            setMenuIsOpen(false);
          }
        }}
        onCreateOption={onCreateOption}
        isValidNewOption={() => false}
        placeholder={placeholder}
        className="react-select-container"
        classNamePrefix="react-select"
        isLoading={isLoading}
        styles={customSelectStyles}
        components={{ Option: CustomOption, MenuList: CustomMenuList, SingleValue: CustomSingleValue }}
        onEditOption={onEditOption}
        onDeleteOption={onDeleteOption}
        addNewText={addNewText}
        menuHeader={menuHeader}
        useModal={useModal}
        setMenuIsOpen={setMenuIsOpen}
        onCreateOptionClick={onCreateOption}
        onEditOptionClick={onEditOption}
        inputValue={inputValue}
        onInputChange={(newValue, actionMeta) => {
          if (actionMeta.action === "input-change") {
            setInputValue(newValue);
          } else if (actionMeta.action === "menu-close" || actionMeta.action === "input-blur") {
            if (blockMenuCloseRef.current || isAdding || editingOptionId) {
              return;
            }
            setInputValue("");
          } else if (actionMeta.action === "set-value") {
            setInputValue("");
          }
        }}
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => {
          setMenuIsOpen(true);
          if (typeof document !== 'undefined' && containerRef.current) {
            containerRef.current.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
          }
        }}
        onMenuClose={() => {
          if (blockMenuCloseRef.current) {
            blockMenuCloseRef.current = false;
            return;
          }
          setMenuIsOpen(false);
        }}
        isAdding={isAdding}
        setIsAdding={setIsAdding}
        newVal={newVal}
        setNewVal={setNewVal}
        handleSave={handleSave}
        handleCancel={handleCancel}
        editingOptionId={editingOptionId}
        editingText={editingText}
        setEditingText={setEditingText}
        startEditingOption={startEditingOption}
        stopEditingOption={stopEditingOption}
        blockMenuCloseRef={blockMenuCloseRef}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        menuPlacement="auto"
        triggerDeleteConfirmation={triggerDeleteConfirmation}
        deleteConfirmationMessage={deleteConfirmationMessage}
        {...props}
      />
      <ConfirmModal
        show={showConfirmModal}
        onClose={handleCancelDelete}
        onOk={handleConfirmDelete}
        state="pending"
        messages={{
          pending: {
            title: "Delete Option",
            description: deleteConfirmationMessage || "Are you sure you want to delete this option?",
            actionText: "Delete",
          },
        }}
        buttonColor="error"
      />
    </div>
  );
}
